/**
 * Price markers, label-collision stacks and the popup controller for the
 * diligence offerings map. Hovering a price previews its listing without moving
 * the map; a click, Enter or Space pins it; stacks and count bubbles open a list.
 * A registry of the element that currently represents each listing (its pin, a
 * chip in a stack, or the group control) lets an open popup follow re-clustering.
 * Plain DOM and Maps objects: the component mounts this per cohort and renders
 * the `PopupView` it is handed into the overlay shell.
 */
import { MarkerClusterer, type Cluster, type Marker } from '@googlemaps/markerclusterer'
import { hasMapPosition, type MarketOffering, type MarketTransactionType } from '../../../api/marketOfferings'
import type { GoogleMapsLibraries } from '../../../services/googleMaps'
import { BUBBLE_FALLBACK, LabelCollisionAlgorithm, PRICE_LABEL_FALLBACK, stackPlan } from '../../../services/mapLabelClusters'
import type { MapPopupOverlay, PopupAnchor, PopupMode } from '../../../services/mapPopup'
import type { PixelSize, PopupSide } from '../../../services/mapPopupPlacement'
import { money } from './format'
import { byEvidence } from './OfferingPopup'

export type PopupView =
  | { kind: 'single'; offering: MarketOffering; pinned: boolean }
  | { kind: 'group'; key: string; offerings: MarketOffering[]; zoom?: () => void }

export interface OfferingMarkers {
  /** Close the popup; `returnFocus` sends focus back to a pinned card's marker. */
  close: (returnFocus: boolean) => void
  dispose: () => void
}

type AdvancedMarker = google.maps.marker.AdvancedMarkerElement
/**
 * What represents a listing or a group on the map now: the element the popup
 * points at, the marker content holding it (anchored bottom-centre on
 * `position`), the control carrying the popup's ARIA state, and its marker.
 * `focus`: where keyboard focus keeps a preview open; `describes`: what a
 * preview describes (default: the owner).
 */
interface Rep {
  element: HTMLElement; root: HTMLElement; owner: HTMLElement; marker: AdvancedMarker; position: google.maps.LatLng | google.maps.LatLngLiteral; prefer?: PopupSide
  focus?: HTMLElement; describes?: readonly HTMLElement[]
}
interface GroupRep extends Rep { key: string; offerings: MarketOffering[]; tight: boolean; bounds: google.maps.LatLngBounds | undefined }
type Target = { kind: 'single'; offering: MarketOffering } | { kind: 'group'; key: string }
/** `invoker`: the control that pinned the card, where a dismissal returns focus (null for a preview). */
interface Active { target: Target; mode: PopupMode; rep: Rep; raised: { marker: AdvancedMarker; zIndex: number | null | undefined } | null; invoker: HTMLElement | null }

/** A preview closes this long after the pointer has left both its price and the popup. */
const HOVER_CLOSE_MS = 150
/** A pinned card's marker draws above every other marker, the subject house included. */
const PINNED_Z_INDEX = 3000

function control(className: string, text: string, label: string): HTMLButtonElement {
  const button = document.createElement('button')
  button.type = 'button'
  button.className = className
  button.textContent = text
  button.setAttribute('aria-label', label)
  button.setAttribute('aria-haspopup', 'dialog')
  return button
}

const placeOf = (offering: MarketOffering) => offering.address || offering.postcode
/** What follows the place in a chip's label and a single pin's name; the preview hides these as an echo. */
const priceFacts = (offering: MarketOffering, transactionType: MarketTransactionType) =>
  ` · ${money(offering.pricePence)}${transactionType === 'rent' ? ' per month' : ''}${offering.usedInAssessment ? ' · used in assessment' : ''}`

function priceChip(offering: MarketOffering, transactionType: MarketTransactionType): HTMLButtonElement {
  const chip = control(`map-pin is-price is-${transactionType}${offering.usedInAssessment ? ' is-used' : ''}`,
    `${offering.usedInAssessment ? '✓ ' : ''}${money(offering.pricePence, transactionType === 'sale')}`,
    `Open ${placeOf(offering) || 'listing'}${priceFacts(offering, transactionType)}`)
  chip.dataset.offeringId = offering.id
  return chip
}

const moreChip = (more: number, total: number, transactionType: MarketTransactionType) =>
  control(`map-pin is-price is-more is-${transactionType}`, `+${more} more`, `Show all ${total} listings at this map position`)

const countBubble = (count: number, used: number, transactionType: MarketTransactionType) =>
  control(`map-cluster is-offers is-${transactionType}${used ? ' is-used' : ''}`, `${used ? '✓ ' : ''}${count}`, `Open ${count} listings${used ? ` · ${used} used in assessment` : ''}`)

const anchorOf = (rep: Rep): PopupAnchor => ({ element: rep.element, root: rep.root, position: rep.position })

export function mountOfferingMarkers({ loaded, map, container, rows, transactionType, popup, show, busy, onOpen }: {
  loaded: GoogleMapsLibraries; map: google.maps.Map; container: HTMLElement; rows: MarketOffering[]; transactionType: MarketTransactionType
  popup: MapPopupOverlay
  /** Render the popup content; `sync` renders before returning, so content and placement land in one frame. */
  show: (view: PopupView | null, sync: boolean) => void
  busy: () => boolean
  /** Called whenever a popup opens: the initial viewport fitting ends at user input. */
  onOpen: () => void
}): OfferingMarkers {
  const events = new AbortController()
  const { signal } = events
  const { AdvancedMarkerElement } = loaded.marker
  const shell = popup.shell

  // Measure every price label first: collisions use the real label widths.
  const entries = rows.filter(hasMapPosition).map(offering => ({ offering, chip: priceChip(offering, transactionType) }))
  const moreSample = moreChip(99, 99, transactionType)
  const bubbleSample = countBubble(99, 99, transactionType)
  const measurer = document.createElement('div')
  measurer.className = 'dil-map-measure'
  measurer.setAttribute('aria-hidden', 'true')
  measurer.append(...entries.map(entry => entry.chip), moreSample, bubbleSample)
  container.append(measurer)
  const sizeOf = (element: HTMLElement, fallback: PixelSize): PixelSize =>
    element.offsetWidth > 0 && element.offsetHeight > 0 ? { width: element.offsetWidth, height: element.offsetHeight } : fallback
  const chipSizes = entries.map(entry => sizeOf(entry.chip, PRICE_LABEL_FALLBACK))
  const moreSize = sizeOf(moreSample, PRICE_LABEL_FALLBACK)
  const bubbleSize = sizeOf(bubbleSample, BUBBLE_FALLBACK)
  measurer.remove()

  const byMarker = new Map<Marker, MarketOffering>()
  const singles = new Map<Marker, Rep>()
  const sizes = new WeakMap<Marker, PixelSize>()
  const offeringReps = new Map<string, Rep>()
  const groupReps = new Map<string, GroupRep>()
  const rendered = new WeakMap<Cluster, { group: GroupRep; members: Map<string, Rep> }>()
  const algorithm = new LabelCollisionAlgorithm({
    sizeOf: marker => sizes.get(marker) ?? PRICE_LABEL_FALLBACK,
    order: (a, b) => { const x = byMarker.get(a), y = byMarker.get(b); return x && y ? byEvidence(x, y) : 0 },
    moreSize, bubbleSize,
  })

  let active: Active | null = null
  let suppressFocusPreview = false
  /** The stack chip under the current pointer press, so the stack's gmp-click for it pins that chip. */
  let pressed: string | null = null
  let closeTimer: number | undefined
  let reanchorFrame = 0

  const described = (rep: Rep) => rep.describes ?? [rep.owner]
  const release = (current: Active) => {
    current.rep.owner.removeAttribute('aria-expanded')
    delete current.rep.root.dataset.open
    for (const element of described(current.rep)) if (element.getAttribute('aria-describedby') === shell.id) element.removeAttribute('aria-describedby')
    if (current.raised) current.raised.marker.zIndex = current.raised.zIndex
    current.raised = null
  }
  const claim = (current: Active) => {
    if (current.mode === 'preview') { for (const element of described(current.rep)) element.setAttribute('aria-describedby', shell.id); return }
    current.rep.owner.setAttribute('aria-expanded', 'true')
    // A stack's own wrapper owns its list; the chips it holds show the highlight.
    if (current.rep.owner === current.rep.marker) current.rep.root.dataset.open = ''
    current.raised = { marker: current.rep.marker, zIndex: current.rep.marker.zIndex }
    current.rep.marker.zIndex = PINNED_Z_INDEX
  }
  const sameSingle = (id: string) => active?.target.kind === 'single' && active.target.offering.id === id

  const open = (target: Target, mode: PopupMode, rep: Rep, view: PopupView, invoker: HTMLElement | null) => {
    window.clearTimeout(closeTimer)
    const previous = active
    if (previous) release(previous)
    const next: Active = { target, mode, rep, raised: null, invoker: mode === 'pinned' ? invoker : null }
    active = next
    claim(next)
    onOpen()
    popup.open(anchorOf(rep), mode, { instant: previous?.mode === 'preview' && mode === 'preview', prefer: rep.prefer })
    show(view, true)
  }

  /**
   * `marker`: a user dismissal returns focus to the control that pinned the card, or its marker; `map`: to the map.
   * Focus inside the card never stays behind; focus elsewhere on the page (another control or overlay) stays put.
   */
  const close = (focus: 'marker' | 'map' | null) => {
    window.clearTimeout(closeTimer)
    cancelAnimationFrame(reanchorFrame)
    const current = active
    if (!current) return
    const focusInside = shell.contains(document.activeElement)
    const focusInMap = focusInside || container.contains(document.activeElement) || document.activeElement === document.body
    active = null
    release(current)
    popup.close()
    show(null, false)
    // The marker element itself is the focusable wrapper (role="button", roving tabindex); a stack chip is its own stop.
    const marker = current.rep.marker
    const invoker = current.invoker
    const back = invoker?.isConnected && marker.contains(invoker) ? invoker : marker
    const target = focus === 'marker' && current.mode === 'pinned' && marker.isConnected && focusInMap ? back : focus === 'map' || focusInside ? container : null
    if (!target) return
    // Returning focus to a pin must not reopen its preview.
    suppressFocusPreview = true
    try { target.focus({ preventScroll: true }) } finally { suppressFocusPreview = false }
  }

  const preview = (offering: MarketOffering, rep: Rep) => {
    window.clearTimeout(closeTimer)
    if (busy() || active?.mode === 'pinned' || sameSingle(offering.id)) return
    open({ kind: 'single', offering }, 'preview', rep, { kind: 'single', offering, pinned: false }, null)
  }
  /** Idempotent: a chip's click and its marker's gmp-click may both report one activation. */
  const pinOffering = (offering: MarketOffering, rep: Rep, invoker: HTMLElement | null) => {
    if (busy() || (active?.mode === 'pinned' && sameSingle(offering.id))) return
    open({ kind: 'single', offering }, 'pinned', rep, { kind: 'single', offering, pinned: true }, invoker)
  }
  const openGroup = (group: GroupRep, invoker: HTMLElement | null) => {
    if (busy() || (active?.target.kind === 'group' && active.target.key === group.key)) return
    const { bounds } = group
    // Members spread over more than 30 m separate at a closer zoom; a shared position never does.
    const zoom = group.tight || !bounds ? undefined : () => { map.fitBounds(bounds, 24); close('map') }
    open({ kind: 'group', key: group.key }, 'pinned', group, { kind: 'group', key: group.key, offerings: group.offerings, zoom }, invoker)
  }

  // Hover is read from :hover when deciding: a chip removed by re-clustering never reports pointerleave.
  const hovered = (current: Active) => current.rep.element.matches(':hover') || shell.matches(':hover')
  // A preview whose listing holds keyboard focus closes on focusout, not when a pointer leaves some marker.
  const focusedOn = (current: Active) => current.rep.focus?.contains(document.activeElement) ?? false
  const scheduleClose = () => {
    window.clearTimeout(closeTimer)
    if (active?.mode !== 'preview') return
    closeTimer = window.setTimeout(() => {
      if (active?.mode === 'preview' && !hovered(active) && !focusedOn(active)) close(null)
    }, HOVER_CLOSE_MS)
  }
  const enter = (event: PointerEvent, offering: MarketOffering, rep: Rep) => { if (event.pointerType !== 'touch') preview(offering, rep) }
  const leave = (event: PointerEvent) => { if (event.pointerType !== 'touch') scheduleClose() }
  const focusIn = (offering: MarketOffering, rep: Rep) => { if (!suppressFocusPreview) preview(offering, rep) }
  const focusOut = (event: FocusEvent, offering: MarketOffering, scope: HTMLElement) => {
    const next = event.relatedTarget
    if (next instanceof Node && (scope.contains(next) || shell.contains(next))) return
    if (active?.mode === 'preview' && sameSingle(offering.id) && !hovered(active)) close(null)
  }

  shell.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') window.clearTimeout(closeTimer) }, { signal })
  shell.addEventListener('pointerleave', event => { if (event.pointerType !== 'touch') scheduleClose() }, { signal })
  // A pointer click anywhere on a preview pins it; keyboard-generated clicks have detail 0.
  shell.addEventListener('click', event => {
    const current = active
    if (event.detail === 0 || current?.mode !== 'preview' || current.target.kind !== 'single') return
    pinOffering(current.target.offering, current.rep, current.invoker)
  }, { signal })
  // The press is forgotten one macrotask after it ends, after any gmp-click it caused.
  const endPress = () => {
    const gesture = pressed
    if (gesture !== null) window.setTimeout(() => { if (pressed === gesture) pressed = null }, 0)
  }
  window.addEventListener('pointerup', endPress, { capture: true, signal })
  window.addEventListener('pointercancel', endPress, { capture: true, signal })
  const mapListeners = [
    map.addListener('click', () => close('marker')),
    map.addListener('dragstart', () => { if (active?.mode === 'preview') close(null) }),
  ]

  const markers = entries.map(({ offering, chip }, index) => {
    const position = { lat: offering.latitude!, lng: offering.longitude! }
    // The wrapper is the roving tab stop: its name carries the price the preview hides as an echo.
    const marker = new AdvancedMarkerElement({ position, content: chip, title: `${placeOf(offering) || 'Property listing'}${priceFacts(offering, transactionType)}`, gmpClickable: true })
    // Focus may sit on the wrapper or on the chip inside it; a preview describes whichever has it.
    const rep: Rep = { element: chip, root: chip, owner: chip, marker, position, focus: marker, describes: [chip, marker] }
    byMarker.set(marker, offering)
    singles.set(marker, rep)
    sizes.set(marker, chipSizes[index] ?? PRICE_LABEL_FALLBACK)
    // The SDK host owns click and keyboard activation; the chip's own click covers its separate tab stop.
    marker.addEventListener('gmp-click', () => pinOffering(offering, rep, marker), { signal })
    chip.addEventListener('click', () => pinOffering(offering, rep, chip), { signal })
    marker.addEventListener('pointerenter', event => enter(event, offering, rep), { signal })
    marker.addEventListener('pointerleave', leave, { signal })
    marker.addEventListener('focusin', () => focusIn(offering, rep), { signal })
    marker.addEventListener('focusout', event => focusOut(event, offering, marker), { signal })
    return marker
  })

  // Group markers are rebuilt on every regrouping; their listeners retire with their generation.
  let generation = { revision: -1, events: new AbortController() }
  const retired: AbortController[] = []
  const groupSignal = () => {
    if (generation.revision !== algorithm.revision) {
      retired.push(generation.events)
      generation = { revision: algorithm.revision, events: new AbortController() }
    }
    return generation.events.signal
  }

  const renderGroup = (cluster: Cluster): Marker => {
    const signal = groupSignal()
    // The algorithm lists members in stack order: in assessment first, then price ascending.
    const offerings = cluster.markers.flatMap(marker => { const offering = byMarker.get(marker); return offering ? [offering] : [] })
    const tight = algorithm.groupOf(cluster)?.tight ?? false
    const key = offerings.map(offering => offering.id).sort().join('\n')
    const position = cluster.position
    const bounds = cluster.bounds
    const plan = stackPlan(offerings.length, tight)
    const zIndex = 1000 + offerings.length
    const members = new Map<string, Rep>()
    if (plan.kind === 'bubble') {
      const content = countBubble(offerings.length, offerings.filter(offering => offering.usedInAssessment).length, transactionType)
      const marker = new AdvancedMarkerElement({ position, content, gmpClickable: true, zIndex })
      const group: GroupRep = { key, element: content, root: content, owner: content, marker, position, offerings, tight, bounds }
      for (const offering of offerings) members.set(offering.id, group)
      marker.addEventListener('gmp-click', () => openGroup(group, marker), { signal })
      content.addEventListener('click', () => openGroup(group, content), { signal })
      rendered.set(cluster, { group, members })
      return marker
    }
    const root = document.createElement('div')
    root.className = 'map-stack'
    const shown = offerings.slice(0, plan.shown)
    const marker = new AdvancedMarkerElement({
      position, content: root, gmpClickable: true, zIndex,
      title: `${offerings.length} listings: ${shown.map(offering => money(offering.pricePence, transactionType === 'sale')).join(', ')}${plan.more ? ` and ${plan.more} more` : ''}`,
    })
    const more = plan.more > 0 ? moreChip(plan.more, offerings.length, transactionType) : null
    // The list's ARIA state sits on a real control: "+N more", else the stack's role="button" wrapper.
    const group: GroupRep = { key, element: root, root, owner: more ?? marker, marker, position, offerings, tight, bounds }
    // Set when a chip's own click handled this press; the next press or key on the stack clears it.
    let chipHandled = false
    marker.addEventListener('pointerdown', () => { chipHandled = false }, { capture: true, signal })
    marker.addEventListener('keydown', () => { chipHandled = false }, { capture: true, signal })
    for (const offering of shown) {
      const chip = priceChip(offering, transactionType)
      // Beside the stack, so the popup never covers the neighbouring chips.
      // Its focus scope is the chip alone: the stack's wrapper also holds the sibling chips.
      const rep: Rep = { element: chip, root, owner: chip, marker, position, prefer: 'right', focus: chip }
      members.set(offering.id, rep)
      chip.addEventListener('pointerenter', event => enter(event, offering, rep), { signal })
      chip.addEventListener('pointerleave', leave, { signal })
      chip.addEventListener('pointerdown', () => { pressed = offering.id }, { signal })
      chip.addEventListener('click', event => { event.stopPropagation(); chipHandled = true; pinOffering(offering, rep, chip) }, { signal })
      chip.addEventListener('focusin', () => focusIn(offering, rep), { signal })
      chip.addEventListener('focusout', event => focusOut(event, offering, chip), { signal })
      root.append(chip)
    }
    if (more) {
      const rep: Rep = { element: more, root, owner: more, marker, position, prefer: 'right' }
      for (const offering of offerings.slice(plan.shown)) members.set(offering.id, rep)
      more.addEventListener('pointerdown', () => { pressed = null }, { signal })
      more.addEventListener('click', event => { event.stopPropagation(); chipHandled = true; openGroup(group, more) }, { signal })
      root.append(more)
    }
    // A press on a chip pins that chip; keyboard activation of the stack itself opens its list.
    marker.addEventListener('gmp-click', () => {
      const id = pressed
      const rep = id === null ? undefined : members.get(id)
      const offering = id === null ? undefined : shown.find(member => member.id === id)
      if (rep && offering) pinOffering(offering, rep, rep.element)
      else if (!chipHandled) openGroup(group, marker)
    }, { signal })
    rendered.set(cluster, { group, members })
    return marker
  }

  /** Re-resolve the open popup's anchor after the clusterer has swapped its markers; close when nothing represents it. */
  const follow = () => {
    const current = active
    if (!current) return
    const rep = current.target.kind === 'single' ? offeringReps.get(current.target.offering.id) : groupReps.get(current.target.key)
    if (!rep) { close(null); return }
    if (rep === current.rep) return
    release(current)
    current.rep = rep
    // The control that pinned the card stays its focus return only while it still belongs to the new marker.
    if (current.invoker && !(current.invoker.isConnected && rep.marker.contains(current.invoker))) current.invoker = null
    claim(current)
    popup.reanchor(anchorOf(rep))
    if (current.mode === 'preview' && !hovered(current)) scheduleClose()
  }

  let seen = 0
  const clustered = () => {
    if (algorithm.revision === seen) return
    seen = algorithm.revision
    groupSignal()
    for (const retiredEvents of retired.splice(0)) retiredEvents.abort()
    offeringReps.clear()
    groupReps.clear()
    for (const cluster of algorithm.clusters) {
      const [only] = cluster.markers
      const single = only && cluster.markers.length === 1 ? singles.get(only) : undefined
      const offering = only ? byMarker.get(only) : undefined
      if (single && offering) { offeringReps.set(offering.id, single); continue }
      const drawn = rendered.get(cluster)
      if (!drawn) continue
      groupReps.set(drawn.group.key, drawn.group)
      drawn.members.forEach((rep, id) => offeringReps.set(id, rep))
    }
    if (active) { cancelAnimationFrame(reanchorFrame); reanchorFrame = requestAnimationFrame(follow) }
  }

  // onClusterClick is a no-op: the renderer attaches its own activation listeners.
  const clusterer = new MarkerClusterer({ markers, algorithm, renderer: { render: renderGroup }, onClusterClick: () => {} })
  const clusteringEnd = google.maps.event.addListener(clusterer, 'clusteringend', clustered)
  clusterer.setMap(map)

  return {
    close: returnFocus => close(returnFocus ? 'marker' : null),
    dispose: () => {
      close(null)
      // Before clearMarkers: its synchronous re-render must not re-resolve anything.
      clusteringEnd.remove()
      mapListeners.forEach(listener => listener.remove())
      events.abort()
      generation.events.abort()
      retired.splice(0).forEach(retiredEvents => retiredEvents.abort())
      window.clearTimeout(closeTimer)
      cancelAnimationFrame(reanchorFrame)
      clusterer.clearMarkers()
      clusterer.setMap(null)
      markers.forEach(marker => { marker.map = null })
      measurer.remove()
    },
  }
}
